// src/pages/NetworkAlerts/SearchTab.js — aba "Buscar clientes" (busca por topologia no ERP)
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import AddAlertOutlinedIcon from "@material-ui/icons/AddAlertOutlined";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TravelExploreIcon from "@material-ui/icons/ExploreOutlined";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import StorageOutlinedIcon from "@material-ui/icons/StorageOutlined";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmptyState from "../../components/EmptyState";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";

import useStyles from "./styles";
import GroupPicker, { GROUP_BY_TYPES } from "./GroupPicker";
import {
  SCOPE_TYPES,
  TOPOLOGY_FIELDS,
  scopeTypeLabel,
  scopePlaceholder,
  toNumber,
} from "./constants";

const EMPTY_RESULT = {
  items: [],
  count: 0,
  hasMore: false,
  unsupported: [],
  source: null,
  indexEmpty: false,
};

// Só o SGP usa índice local; o IXC busca ao vivo (`source: 'live'`).
const INDEXED_ERP_TYPES = ["SGP"];

const INDEX_POLL_MS = 15000;

const normalizeResult = (data) => ({
  items: Array.isArray(data?.items) ? data.items : [],
  count: toNumber(data?.count),
  hasMore: !!data?.hasMore,
  unsupported: Array.isArray(data?.unsupported)
    ? data.unsupported.map((f) => String(f).toLowerCase())
    : [],
  source: data?.source || null,
  indexEmpty: !!data?.indexEmpty,
});

const joinPhones = (phones) =>
  (Array.isArray(phones) ? phones : [phones]).filter(Boolean).join(", ");

const joinPlace = (item) => [item.city, item.district].filter(Boolean).join(" / ");

const SearchTab = ({ erps, onCreateAlert, onBroadcastNow }) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();
  const { user } = useContext(AuthContext);
  const isAdmin = !!user && (user.profile === "admin" || !!user.super);

  const [erpIntegrationId, setErpIntegrationId] = useState(
    erps.length === 1 ? String(erps[0].id) : ""
  );
  const [by, setBy] = useState("pon");
  const [value, setValue] = useState("");
  // ERPs indexados: agrupamento + grupo escolhido (a busca dispara ao selecionar)
  const [groupBy, setGroupBy] = useState(GROUP_BY_TYPES[0]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupsReloadKey, setGroupsReloadKey] = useState(0);
  const prevIndexRunning = useRef(false);
  const [loading, setLoading] = useState(false);
  // Último escopo pesquisado (o formulário pode mudar sem nova busca)
  const [query, setQuery] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [result, setResult] = useState(null);

  // Índice local (SGP): { running, page, fetched, total, indexed, startedAt, finishedAt, error, indexedAt, rows }
  const [indexStatus, setIndexStatus] = useState(null);
  const [reindexing, setReindexing] = useState(false);

  // Se a lista de ERPs chegar depois do primeiro render com um único item, seleciona.
  if (!erpIntegrationId && erps.length === 1) {
    setErpIntegrationId(String(erps[0].id));
  }

  const selectedErp = erps.find((it) => String(it.id) === erpIntegrationId);
  const erpUsesIndex = INDEXED_ERP_TYPES.includes(String(selectedErp?.type || "").toUpperCase());
  // A resposta da busca é a fonte definitiva; antes dela, decide pelo tipo do ERP.
  const showIndex =
    !!erpIntegrationId && (result?.source ? result.source === "index" : erpUsesIndex);

  const fetchIndexStatus = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/network-alerts/index-status/${id}`);
      setIndexStatus(data || null);
    } catch (err) {
      toastError(err);
    }
  }, []);

  // Ao trocar de ERP: limpa o resultado e carrega o status do índice (só ERPs indexados).
  useEffect(() => {
    setIndexStatus(null);
    setSelectedGroup(null);
    if (!erpIntegrationId || !erpUsesIndex) return;
    fetchIndexStatus(erpIntegrationId);
  }, [erpIntegrationId, erpUsesIndex, fetchIndexStatus]);

  // Quando a indexação termina (running true → false), recarrega os grupos.
  useEffect(() => {
    const running = !!indexStatus?.running;
    if (prevIndexRunning.current && !running) {
      setGroupsReloadKey((k) => k + 1);
    }
    prevIndexRunning.current = running;
  }, [indexStatus?.running]);

  // Enquanto indexa, atualiza a cada 15 s; para sozinho quando `running` vira false.
  useEffect(() => {
    if (!indexStatus?.running || !erpIntegrationId) return undefined;
    const timer = setInterval(() => fetchIndexStatus(erpIntegrationId), INDEX_POLL_MS);
    return () => clearInterval(timer);
  }, [indexStatus?.running, erpIntegrationId, fetchIndexStatus]);

  const runSearch = async (params, page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/network-alerts/search", {
        params: { ...params, pageNumber: page },
      });
      setResult(normalizeResult(data));
      setQuery(params);
      setPageNumber(page);
      if (data?.source === "index") {
        setIndexStatus((prev) => ({
          ...(prev || {}),
          ...(data.indexing || {}),
          indexedAt: data.indexedAt ?? prev?.indexedAt ?? null,
        }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const v = value.trim();
    if (!erpIntegrationId || !v) return;
    runSearch({ erpIntegrationId: Number(erpIntegrationId), by, value: v }, 1);
  };

  const handleGroupByChange = (next) => {
    setGroupBy(next);
    setSelectedGroup(null);
    setResult(null);
    setQuery(null);
  };

  // Selecionar um grupo já dispara a busca exata (página 1).
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    if (!group || !erpIntegrationId) {
      setResult(null);
      setQuery(null);
      return;
    }
    runSearch(
      {
        erpIntegrationId: Number(erpIntegrationId),
        by: groupBy,
        value: group.value,
        exact: 1,
      },
      1
    );
  };

  // Meta da resposta de /groups (indexedAt/rows) mantém a barra de status em dia.
  const handleGroupsLoaded = useCallback(({ rows, indexedAt }) => {
    setIndexStatus((prev) => {
      if (prev?.running) return prev;
      return {
        ...(prev || {}),
        rows,
        indexedAt: indexedAt ?? prev?.indexedAt ?? null,
      };
    });
  }, []);

  const goToPage = (page) => {
    if (!query || page < 1) return;
    runSearch(query, page);
  };

  const handleReindex = async () => {
    if (!erpIntegrationId || reindexing) return;
    setReindexing(true);
    try {
      const { data } = await api.post(`/network-alerts/reindex/${erpIntegrationId}`);
      toast.success(i18n.t("networkAlerts.index.started"));
      if (data && typeof data === "object") {
        setIndexStatus((prev) => ({
          ...(prev || {}),
          ...data,
          running: data.running !== false,
        }));
      } else {
        fetchIndexStatus(erpIntegrationId);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setReindexing(false);
    }
  };

  const res = result || EMPTY_RESULT;
  const unsupported = res.unsupported;
  const hiddenColumns = TOPOLOGY_FIELDS.filter((f) => unsupported.includes(f));
  const searchedByUnsupported = !!query && unsupported.includes(query.by);
  const unsupportedLabels = unsupported
    .filter((f) => SCOPE_TYPES.includes(f))
    .map((f) => scopeTypeLabel(f))
    .join("/");

  const prefill = query
    ? {
        erpIntegrationId: query.erpIntegrationId,
        scopeType: query.by,
        scopeValue: query.value,
        affectedCount: res.count,
      }
    : null;

  const indexRunning = !!indexStatus?.running;
  const indexRows = toNumber(indexStatus?.rows);
  // Índice sem registros: mostra o estado vazio no lugar dos selects.
  const indexIsEmpty =
    showIndex && !!indexStatus && !indexRunning && indexRows === 0 && !indexStatus.indexedAt;
  const groupPickerBlocked = showIndex && (!indexStatus || indexRunning || indexIsEmpty);

  const reindexButton = (
    <Button
      size="small"
      variant="outlined"
      className={classes.btnWrapper}
      startIcon={<AutorenewIcon />}
      onClick={handleReindex}
      disabled={reindexing || indexRunning}
    >
      {i18n.t("networkAlerts.index.reindex")}
      {reindexing && <CircularProgress size={18} className={classes.buttonProgress} />}
    </Button>
  );

  const renderIndexLine = () => {
    if (!showIndex) return null;
    let text;
    if (indexRunning) {
      text = i18n.t("networkAlerts.index.running", {
        page: toNumber(indexStatus.page),
        indexed: toNumber(indexStatus.indexed),
      });
    } else if (indexStatus?.indexedAt || indexRows > 0) {
      text = i18n.t("networkAlerts.index.updatedAt", {
        date: indexStatus.indexedAt ? datetimeToClient(indexStatus.indexedAt) : "—",
        rows: indexRows,
      });
    } else if (indexStatus) {
      text = i18n.t("networkAlerts.index.never");
    } else {
      text = i18n.t("networkAlerts.index.loading");
    }
    return (
      <div className={classes.indexLine} aria-live="polite">
        <StorageOutlinedIcon />
        <span className={classes.indexText}>
          {text}
          {indexStatus?.error && !indexRunning && (
            <span className={classes.indexError}>
              {" · "}
              {i18n.t("networkAlerts.index.error", {
                error: indexStatus.error,
              })}
            </span>
          )}
        </span>
        {indexRunning && <LinearProgress className={classes.indexProgress} color="primary" />}
        {isAdmin && reindexButton}
      </div>
    );
  };

  if (erps.length === 0) {
    return (
      <Paper variant="outlined" className={classes.card}>
        <EmptyState
          icon={<LinkOffIcon />}
          title={i18n.t("networkAlerts.empty.noErp.title")}
          message={i18n.t("networkAlerts.empty.noErp.message")}
          action={
            <Button component={RouterLink} to="/queue-integration" variant="outlined">
              {i18n.t("networkAlerts.buttons.goToIntegrations")}
            </Button>
          }
        />
      </Paper>
    );
  }

  const renderTableBody = () => (
    <Table size="small" stickyHeader className={classes.searchTable}>
      <TableHead>
        <TableRow>
          <TableCell>{i18n.t("networkAlerts.table.name")}</TableCell>
          <TableCell>{i18n.t("networkAlerts.table.contract")}</TableCell>
          <TableCell>{i18n.t("networkAlerts.table.phones")}</TableCell>
          <TableCell>{i18n.t("networkAlerts.table.city")}</TableCell>
          {!hiddenColumns.includes("olt") && (
            <TableCell>{i18n.t("networkAlerts.table.olt")}</TableCell>
          )}
          {!hiddenColumns.includes("pon") && (
            <TableCell>{i18n.t("networkAlerts.table.pon")}</TableCell>
          )}
          {!hiddenColumns.includes("nas") && (
            <TableCell>{i18n.t("networkAlerts.table.nas")}</TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && <TableRowSkeleton columns={7 - hiddenColumns.length} />}
        {!loading &&
          res.items.map((item, idx) => (
            <TableRow key={`${item.customerId || "c"}-${item.contractId || idx}`}>
              <TableCell className={classes.cellPrimary}>{item.customerName || "—"}</TableCell>
              <TableCell className={`${classes.cellSecondary} ${classes.nowrap}`}>
                {item.contractId || "—"}
              </TableCell>
              <TableCell className={classes.cellSecondary}>
                {joinPhones(item.phones) || "—"}
              </TableCell>
              <TableCell className={classes.cellSecondary}>{joinPlace(item) || "—"}</TableCell>
              {!hiddenColumns.includes("olt") && (
                <TableCell className={classes.cellSecondary}>{item.olt || "—"}</TableCell>
              )}
              {!hiddenColumns.includes("pon") && (
                <TableCell className={`${classes.cellSecondary} ${classes.nowrap}`}>
                  {item.pon || "—"}
                </TableCell>
              )}
              {!hiddenColumns.includes("nas") && (
                <TableCell className={classes.cellSecondary}>{item.nas || "—"}</TableCell>
              )}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  const renderIndexEmptyState = () => (
    <EmptyState
      icon={<StorageOutlinedIcon />}
      title={i18n.t("networkAlerts.index.emptyTitle")}
      message={i18n.t(
        isAdmin ? "networkAlerts.index.emptyMessage" : "networkAlerts.index.emptyMessageUser"
      )}
      action={isAdmin ? reindexButton : null}
    />
  );

  const renderCardContent = () => {
    if (showIndex && indexRunning && !result) {
      return (
        <EmptyState
          icon={<AutorenewIcon />}
          title={i18n.t("networkAlerts.groups.indexing.title")}
          message={i18n.t("networkAlerts.groups.indexing.message")}
        />
      );
    }
    if (showIndex && indexIsEmpty && !result) {
      return renderIndexEmptyState();
    }
    if (!result && !loading) {
      return (
        <EmptyState
          icon={<TravelExploreIcon />}
          title={i18n.t(
            showIndex ? "networkAlerts.groups.idle.title" : "networkAlerts.search.idle.title"
          )}
          message={i18n.t(
            showIndex ? "networkAlerts.groups.idle.message" : "networkAlerts.search.idle.message"
          )}
        />
      );
    }
    if (!loading && res.indexEmpty) {
      return renderIndexEmptyState();
    }
    // fora da tabela: dentro dela o aviso ficaria centrado na largura mínima, fora da tela no celular
    if (!loading && result && res.items.length === 0) {
      return (
        <EmptyState
          title={i18n.t("networkAlerts.search.empty.title")}
          message={i18n.t("networkAlerts.search.empty.message")}
        />
      );
    }
    return renderTableBody();
  };

  return (
    <div className={classes.tabPanel}>
      <form className={classes.filterBar} onSubmit={handleSubmit} noValidate>
        {erps.length > 1 && (
          <TextField
            select
            className={classes.filterItem}
            value={erpIntegrationId}
            onChange={(e) => {
              setErpIntegrationId(e.target.value);
              setSelectedGroup(null);
              setResult(null);
              setQuery(null);
            }}
            variant="outlined"
            size="small"
            SelectProps={{
              displayEmpty: true,
              renderValue: () =>
                selectedErp ? (
                  `${selectedErp.name} (${selectedErp.type})`
                ) : (
                  <span className={classes.placeholder}>{i18n.t("networkAlerts.search.erp")}</span>
                ),
            }}
            inputProps={{ "aria-label": i18n.t("networkAlerts.search.erp") }}
          >
            {erps.map((it) => (
              <MenuItem key={it.id} value={String(it.id)}>
                {it.name} ({it.type})
              </MenuItem>
            ))}
          </TextField>
        )}
        {showIndex && (
          <GroupPicker
            erpIntegrationId={erpIntegrationId}
            by={groupBy}
            onByChange={handleGroupByChange}
            selected={selectedGroup}
            onSelect={handleGroupSelect}
            reloadKey={groupsReloadKey}
            disabled={groupPickerBlocked}
            onLoaded={handleGroupsLoaded}
          />
        )}
        {!showIndex && (
          <>
            <TextField
              select
              className={classes.filterItem}
              value={by}
              onChange={(e) => setBy(e.target.value)}
              variant="outlined"
              size="small"
              SelectProps={{
                renderValue: (v) => (
                  <>
                    <span className={classes.filterPrefix}>
                      {i18n.t("networkAlerts.search.by")}:
                    </span>{" "}
                    {scopeTypeLabel(v)}
                  </>
                ),
              }}
              inputProps={{ "aria-label": i18n.t("networkAlerts.search.by") }}
            >
              {SCOPE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {scopeTypeLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.filterItemWide}
              placeholder={scopePlaceholder(by)}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" className={classes.placeholder} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "aria-label": i18n.t("networkAlerts.search.value"),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.btnWrapper}
              disabled={loading || !erpIntegrationId || !value.trim()}
            >
              {i18n.t("networkAlerts.buttons.search")}
              {loading && <CircularProgress size={20} className={classes.buttonProgress} />}
            </Button>
          </>
        )}
      </form>

      {renderIndexLine()}

      {result && unsupportedLabels && (
        <div className={classes.warning} role="alert">
          <WarningRoundedIcon />
          <span>
            {searchedByUnsupported
              ? i18n.t("networkAlerts.search.unsupportedBy", {
                  field: scopeTypeLabel(query.by),
                })
              : i18n.t("networkAlerts.search.unsupported", {
                  fields: unsupportedLabels,
                })}
          </span>
        </div>
      )}

      <Paper variant="outlined" className={classes.card}>
        {result && !res.indexEmpty && (
          <div className={classes.cardHead}>
            <div className={classes.cardHeadTitle}>
              <b>{res.count}</b>
              <span>{i18n.t("networkAlerts.search.total", { count: res.count })}</span>
            </div>
            <div className={classes.cardHeadActions}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddAlertOutlinedIcon />}
                onClick={() => onCreateAlert(prefill)}
                disabled={!prefill}
              >
                {i18n.t("networkAlerts.buttons.createAlert")}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SendOutlinedIcon />}
                onClick={() => onBroadcastNow(prefill)}
                disabled={!prefill || res.count === 0}
              >
                {i18n.t("networkAlerts.buttons.broadcastNow")}
              </Button>
            </div>
          </div>
        )}
        <div className={classes.tableWrap}>{renderCardContent()}</div>
        {result && !res.indexEmpty && (res.hasMore || pageNumber > 1) && (
          <div className={classes.pagination}>
            <span>{i18n.t("networkAlerts.search.page", { page: pageNumber })}</span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={() => goToPage(pageNumber - 1)}
              disabled={loading || pageNumber <= 1}
            >
              {i18n.t("networkAlerts.buttons.prev")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              onClick={() => goToPage(pageNumber + 1)}
              disabled={loading || !res.hasMore}
            >
              {i18n.t("networkAlerts.buttons.next")}
            </Button>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default SearchTab;
